import React, { useState, useEffect, useRef, Suspense } from 'react';
import { useSessionStore } from '@/store/sessionStore';
import { CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Folder, File as FileIcon, DraftingCompass, PlusCircle, Pencil, ArrowDown } from 'lucide-react';
import SessionControls from './SessionControls';
import ConversationDisplay from './ConversationDisplay';
import PromptInput from './PromptInput';
import { searchWorkspace } from '@/lib/api';
import { SearchResultItem } from '@/types/api';
import { toast } from 'sonner';
import { useContextSnippetStore } from '@/store/contextSnippetStore';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useUIStore } from '@/store/uiStore';
import { Loader2 } from 'lucide-react';

// Lazy load heavy components that pull in markdown/mermaid (3MB+) and react-arborist/monaco
const StreamingResponseDisplay = React.lazy(() => import('./StreamingResponseDisplay'));
const ChildSessionsIndicator = React.lazy(() => import('./ChildSessionsIndicator'));
const ContextSnippetEditor = React.lazy(() => import('../editors/context-snippet/ContextSnippetEditor'));

const CurrentSessionPage: React.FC = () => {
  const [prompt, setPrompt] = useState<string>("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerType, setPickerType] = useState<'files' | 'folders' | 'context_snippets' | null>(null);
  const [cursorPosition, setCursorPosition] = useState(0);

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResultItem[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchPage, setSearchPage] = useState(1);
  const [searchHasMore, setSearchHasMore] = useState(false);

  const [editingSnippetState, setEditingSnippetState] = useState<{ isOpen: boolean; snippetId: string | 'new' | null }>({ isOpen: false, snippetId: null });

  const [hasNewMessages, setHasNewMessages] = useState(false);

  const promptInputRef = useRef<HTMLTextAreaElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const prevInputCountRef = useRef(0);

  const {
    currentSessionId,
    isLoading,
    startNewSession,
    setAdHocFiles,
    setAdHocFolders,
    currentSessionDetails,
    setActivePlanInput,
    editingRetryInputId,
    editingRetryPrompt,
    autoScrollEnabled,
    setAutoScrollEnabled,
  } = useSessionStore();

  const inputs = currentSessionDetails?.sessionInputs || [];

  // Context snippets for picker - prefetched by AppInitializer but we need local reference
  const { snippets: contextSnippets, fetchSnippets: fetchContextSnippets } = useContextSnippetStore();
  const { setFocusPromptInput } = useUIStore();
  
  useEffect(() => {
    const focusAction = () => promptInputRef.current?.focus();
    setFocusPromptInput(focusAction);
    return () => setFocusPromptInput(null);
  }, [setFocusPromptInput]);

  useEffect(() => {
    if (!currentSessionId && !isLoading) {
      startNewSession({ session_title: "Initial Session" });
    }
  }, [currentSessionId, isLoading, startNewSession]);

  useEffect(() => {
    if (currentSessionId && !isLoading) {
      setTimeout(() => promptInputRef.current?.focus(), 100);
    }
  }, [currentSessionId, isLoading]);

  // Conditional auto-scroll: only scroll if autoScrollEnabled is true
  useEffect(() => {
    if (viewportRef.current && autoScrollEnabled) {
      const { scrollHeight } = viewportRef.current;
      viewportRef.current.scrollTo({ top: scrollHeight, behavior: 'smooth' });
    }
  }, [inputs.length, autoScrollEnabled]);

  // Track new messages when auto-scroll is disabled
  useEffect(() => {
    const currentCount = inputs.length;
    const prevCount = prevInputCountRef.current;
    prevInputCountRef.current = currentCount;

    if (currentCount > prevCount && !autoScrollEnabled && prevCount > 0) {
      setHasNewMessages(true);
    }
  }, [inputs.length, autoScrollEnabled]);

  // Clear new messages indicator when auto-scroll is re-enabled
  useEffect(() => {
    if (autoScrollEnabled) {
      setHasNewMessages(false);
    }
  }, [autoScrollEnabled]);

  const handleScrollToBottom = () => {
    if (viewportRef.current) {
      const { scrollHeight } = viewportRef.current;
      viewportRef.current.scrollTo({ top: scrollHeight, behavior: 'smooth' });
    }
    setAutoScrollEnabled(true);
    setHasNewMessages(false);
  };

  // Update local prompt state when editingRetryPrompt changes
  useEffect(() => {
    if (editingRetryPrompt !== null) {
      setPrompt(editingRetryPrompt);
    } else {
      setPrompt("");
    }
  }, [editingRetryPrompt]);

  // Debounce search query
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setSearchPage(1);
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  // Fetch search results from API
  useEffect(() => {
    if (!pickerOpen || !pickerType || pickerType === 'context_snippets') {
      return;
    }

    if (!debouncedSearchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    const fetchResults = async () => {
      setSearchLoading(true);
      try {
        const response = await searchWorkspace(debouncedSearchQuery, pickerType, searchPage);
        if (searchPage === 1) {
          setSearchResults(response.results);
        } else {
          setSearchResults(prev => [...prev, ...response.results]);
        }
        setSearchHasMore(response.hasMore);
      } catch (error) {
        toast.error('Failed to search workspace.');
        console.error(error);
      } finally {
        setSearchLoading(false);
      }
    };

    fetchResults();
  }, [debouncedSearchQuery, pickerType, searchPage, pickerOpen]);

  // Reset search query and refocus input when picker closes
  useEffect(() => {
    if (!pickerOpen) {
      setSearchQuery('');
      setTimeout(() => promptInputRef.current?.focus(), 0);
    }
  }, [pickerOpen]);


  const handlePromptKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'ArrowUp' && !e.shiftKey && !e.altKey && !e.ctrlKey && !e.metaKey) {
      if (prompt.length === 0) {
           e.preventDefault();
           const inputs = currentSessionDetails?.sessionInputs || [];
           const navigableInputs = inputs.filter(
              (i) => i.raw_llm_response || (i.aiActions && i.aiActions.length > 0)
           );
           
           if (navigableInputs.length > 0) {
               setActivePlanInput(navigableInputs[navigableInputs.length - 1]);
               e.currentTarget.blur();
           }
           return;
      }
  }

    if (e.key === '@') {
      e.preventDefault();
      setCursorPosition(e.currentTarget.selectionStart);
      setPickerType('files');
      setPickerOpen(true);
    } else if (e.key === '#') {
      e.preventDefault();
      setCursorPosition(e.currentTarget.selectionStart);
      setPickerType('folders');
      setPickerOpen(true);
    } else if (e.key === '!') {
      e.preventDefault();
      setCursorPosition(e.currentTarget.selectionStart);
      setPickerType('context_snippets');
      setPickerOpen(true);
    }
  };

  const handleSelectContextItem = (item: string) => {
    let textToInsert = '';
    if (pickerType === 'files') {
      textToInsert = `@${item}`;
      setAdHocFiles(prev => [...new Set([...prev, item])]);
    } else if (pickerType === 'folders') {
      textToInsert = `#${item}`;
      setAdHocFolders(prev => [...new Set([...prev, item])]);
    } else if (pickerType === 'context_snippets') {
        textToInsert = `!${item}`;
    }

    setPrompt(prev => {
        const before = prev.substring(0, cursorPosition);
        const after = prev.substring(cursorPosition);
        return `${before}${textToInsert} ${after}`;
    });
    setPickerOpen(false);
  };

  const getPickerPlaceholder = () => {
      switch (pickerType) {
          case 'files': return 'Search files... (@)';
          case 'folders': return 'Search folders... (#)';
          case 'context_snippets': return 'Search context snippets... (!)';
          default: return 'Search...';
      }
  }

  const filteredContextSnippets = contextSnippets.filter(s => s.handle.toLowerCase().includes(searchQuery.toLowerCase()));

  return (
      <>
        <Dialog open={editingSnippetState.isOpen} onOpenChange={(open) => !open && setEditingSnippetState({ isOpen: false, snippetId: null })}>
            <DialogContent className="max-w-7xl h-[90vh] p-2">
                {editingSnippetState.snippetId && (
                    <Suspense fallback={<div className="flex items-center justify-center h-full"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>}>
                      <ContextSnippetEditor
                          snippetId={editingSnippetState.snippetId}
                          isModalMode={true}
                          onBack={() => {
                              setEditingSnippetState({ isOpen: false, snippetId: null });
                              fetchContextSnippets();
                          }}
                      />
                    </Suspense>
                )}
            </DialogContent>
        </Dialog>

        <CommandDialog
            open={pickerOpen}
            onOpenChange={setPickerOpen}
            commandProps={{
              shouldFilter: pickerType === 'context_snippets',
            }}
            className="sm:max-w-4xl"
        >
          <CommandInput
              placeholder={getPickerPlaceholder()}
              value={searchQuery}
              onValueChange={setSearchQuery}
          />
          <CommandList>
            {pickerType === 'context_snippets' ? (
                <>
                    <CommandEmpty>No snippets found.</CommandEmpty>
                    <CommandGroup heading="Context Snippets">
                        <CommandItem onSelect={() => {
                            setPickerOpen(false);
                            setEditingSnippetState({ isOpen: true, snippetId: 'new' });
                        }}>
                            <PlusCircle className="mr-2 h-4 w-4" />
                            Create New Snippet...
                        </CommandItem>
                        {filteredContextSnippets.map((snippet) => (
                             <CommandItem
                                key={snippet.id}
                                value={snippet.handle}
                                onSelect={() => handleSelectContextItem(snippet.handle)}
                                className="group"
                             >
                                <div className="flex justify-between items-center w-full">
                                    <div className="flex items-center gap-2">
                                        <DraftingCompass className="mr-2 h-4 w-4" />
                                        <span className="font-mono">{snippet.handle}</span>
                                        <span className="ml-4 text-muted-foreground">{snippet.description}</span>
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-6 w-6"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setPickerOpen(false);
                                            setEditingSnippetState({ isOpen: true, snippetId: snippet.id });
                                        }}
                                    >
                                        <Pencil className="h-3 w-3" />
                                    </Button>
                                </div>
                             </CommandItem>
                        ))}
                    </CommandGroup>
                </>
            ) : (
                <>
                    {searchLoading && searchResults.length === 0 && <CommandEmpty>Loading...</CommandEmpty>}
                    {!searchLoading && searchResults.length === 0 && debouncedSearchQuery.trim() && <CommandEmpty>No results found.</CommandEmpty>}
                    <CommandGroup heading={pickerType === 'files' ? 'Files' : 'Folders'}>
                      {searchResults.map((item) => (
                          <CommandItem key={item.path} value={item.path} onSelect={() => handleSelectContextItem(item.path)}>
                            {item.type === 'file' ? <FileIcon className="mr-2 h-4 w-4" /> : <Folder className="mr-2 h-4 w-4" />}
                            <span>{item.path}</span>
                          </CommandItem>
                      ))}
                    </CommandGroup>
                    {searchHasMore && !searchLoading && (
                        <CommandItem onSelect={() => setSearchPage(p => p + 1)}>
                          Load More...
                        </CommandItem>
                    )}
                </>
            )}

          </CommandList>
        </CommandDialog>

        <div className="relative flex h-full flex-col" onClick={() => setAutoScrollEnabled(false)}>
          <div className="flex-grow min-h-0 overflow-y-auto" ref={viewportRef}>
            <div className="p-2 space-y-2">
              <SessionControls />
              <Suspense fallback={null}>
                <ChildSessionsIndicator />
              </Suspense>
              <div className="rounded-md bg-slate-50 dark:bg-slate-800/30 min-h-[200px]">
                <ConversationDisplay />
              </div>
            </div>

            {/* New messages indicator - inside scroll container */}
            {hasNewMessages && !autoScrollEnabled && (
              <div className="flex justify-center py-2">
                <Button
                  variant="outline"
                  size="icon"
                  className="rounded-full shadow-md bg-background/90 backdrop-blur-sm border border-border/50 hover:bg-background h-8 w-8"
                  onClick={(e) => { e.stopPropagation(); handleScrollToBottom(); }}
                  title="New messages"
                >
                  <ArrowDown className="h-4 w-4" />
                </Button>
              </div>
            )}

            <div className="h-48" /> 
          </div>

          <div className="absolute bottom-0 left-0 w-full bg-gradient-to-t from-background via-background/80 to-transparent pb-1 pt-2 backdrop-blur-sm pointer-events-none">
            <div className="mx-auto max-w-4xl px-1 pointer-events-auto">
                <Suspense fallback={null}>
                  <StreamingResponseDisplay />
                </Suspense>
                <PromptInput
                    ref={promptInputRef}
                    prompt={prompt}
                    setPrompt={setPrompt}
                    handlePromptKeyDown={handlePromptKeyDown}
                />
            </div>
          </div>
        </div>
      </>
  );
};

export default CurrentSessionPage;